// #region Imports
import AddIcon from "@mui/icons-material/Add";
import CloseIcon from "@mui/icons-material/Close";
import DeleteIcon from "@mui/icons-material/DeleteOutlined";
import EditIcon from "@mui/icons-material/EditOutlined";
import Button from "@mui/material/Button";
import Chip from "@mui/material/Chip";
import CircularProgress from "@mui/material/CircularProgress";
import Collapse from "@mui/material/Collapse";
import IconButton from "@mui/material/IconButton";
import List from "@mui/material/List";
import ListItemText from "@mui/material/ListItemText";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import { styled } from "@mui/material/styles";
import { ConfirmResponse, useConfirmDialog } from "_shared/_providers/dialogs/confirm/confirm-dialog";
import DeleteIconButton from "_shared/_views/buttons/delete-icon-button";
import Loader from "_shared/_views/loading/loader";
import Guid from "_shared/guids/guid";
import {
  useDeleteFeeScheduleMutation,
  useGetFeeSchedulesForTeamQuery,
} from "marketplace/teams/fee-schedules/_api/fee-schedule-api";
import { getFeeSchedulesForTeam } from "marketplace/teams/fee-schedules/_stores/fee-schedule-redux-slice";
import EditTeamFeeSchedule from "marketplace/teams/fee-schedules/_views/edit-team-fee-schedule";
import FeeSchedule from "marketplace/teams/fee-schedules/fee-schedule";
import Team from "marketplace/teams/team";
import { enqueueSnackbar } from "_shared/_providers/snackbars/snackbars";
import { useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
// #endregion

// #region Styled Components
const MainContainer = styled("div")(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  gap: theme.spacing(1),
  width: "100%",
}));
const ScheduleList = styled(List)(() => ({
  paddingBottom: 0,
  paddingTop: 0,
}));
/**
 * One schedule: its summary row, and the editor that opens under it (issue #5111).
 *
 * The list item is the `li`, so the editor has to live in a wrapper around it rather than beside it
 * - a second `li` per schedule would make the editor a list entry of its own, and a reader (or a
 * screen reader) would count twice as many fee schedules as the team has.
 */
const ScheduleEntry = styled("li")(({ theme }) => ({
  borderBottom: `1px solid ${theme.palette.divider}`,
  display: "block",
}));
const ScheduleRow = styled("div")(({ theme }) => ({
  alignItems: "center",
  display: "flex",
  gap: theme.spacing(1),
  justifyContent: "space-between",
  paddingBottom: theme.spacing(1),
  paddingTop: theme.spacing(1),
}));
const ScheduleRowActions = styled("div")(() => ({
  flexShrink: 0,
  whiteSpace: "nowrap",
}));
/**
 * The schedule editor where it now lives: under the entry it belongs to, in place of a dialog.
 *
 * Inset and ruled so the form reads as belonging to the row above it rather than as the next row.
 */
const InlineScheduleEditor = styled("div")(({ theme }) => ({
  borderLeft: `3px solid ${theme.palette.primary.main}`,
  paddingBottom: theme.spacing(2),
  paddingLeft: theme.spacing(2),
}));
const NewScheduleEditor = styled(InlineScheduleEditor)(({ theme }) => ({
  marginTop: theme.spacing(1),
}));
const SecondaryLine = styled("span")(({ theme }) => ({
  alignItems: "center",
  display: "flex",
  flexWrap: "wrap",
  gap: theme.spacing(0.5),
}));
const EmptyText = styled(Typography)(({ theme }) => ({
  color: theme.palette.text.secondary,
}));
const AddButtonContainer = styled("div")(() => ({
  display: "flex",
  justifyContent: "flex-start",
}));
// #endregion

// #region Component Input Props
export type ManageTeamFeeSchedulesProps = {
  className?: string;
  /**
   * The team these rates belong to. Taken whole rather than as a list of members, because the
   * category editor keys a memo on that list: built here it is stable, built by a caller that the
   * React Compiler cannot compile it would be a fresh array on every render of the profile form.
   */
  team: Team;
};
// #endregion

/**
 * The team leader's list of the fee schedules their team publishes on its marketplace profile.
 *
 * Publishing rates is optional — a team with none is a normal, complete profile — so the empty state
 * says what the section is for rather than reading as something missing.
 */
/** What `openScheduleEditor` holds for the schedule that does not exist yet. */
const NEW_SCHEDULE_KEY = "new";

export default function ManageTeamFeeSchedules(props: Readonly<ManageTeamFeeSchedulesProps>) {
  const { className, team } = props;
  const teamId = team.id;

  // #region State
  const [deletingScheduleId, setDeletingScheduleId] = useState<Guid | undefined>(undefined);
  /**
   * Which schedule's editor is open: the id of a schedule being edited, or "new".
   *
   * One at a time, the same rule the category editor inside it follows - the form is anchored to a
   * row, and two of them open at once stops saying which row either belongs to.
   */
  const [openScheduleEditor, setOpenScheduleEditor] = useState<string | undefined>(undefined);
  /**
   * Which opening of the new-schedule form is on screen, used as its key so that each opening is
   * a form of its own.
   *
   * `unmountOnExit` unmounts the form only once the collapse has FINISHED closing, and saving
   * closes it. Add another schedule before that transition ends and React reuses the instance
   * that was on its way out — whose name, tags and categories were seeded from props when it
   * mounted, and so are still the ones just saved. An empty rate card arrived carrying the last
   * schedule, saving it published a duplicate, and the category picker offered those rates as
   * already taken.
   */
  const [newScheduleForm, setNewScheduleForm] = useState(0);
  // #endregion

  // #region Hooks
  const confirm = useConfirmDialog();
  const { isError, isLoading } = useGetFeeSchedulesForTeamQuery({ teamId: teamId.value });
  const [deleteFeeSchedule] = useDeleteFeeScheduleMutation();
  const feeSchedulesJson = useSelector(getFeeSchedulesForTeam(teamId));

  // A fresh array each render would reach the list's keys and the editor's props. Memoized
  // deliberately so an open editor is not remounted by an unrelated profile re-render.
  const feeSchedules = useMemo(
    () => (feeSchedulesJson ? FeeSchedule.fromJSONArray(feeSchedulesJson) : []),
    [feeSchedulesJson]
  );

  // Who a category's rate can be assigned to: anyone on the team, the leader included. The API
  // rejects anyone outside it. Memoized deliberately — the category editor keys a memo of its own
  // on this list, so it has to hold its identity between renders.
  const teamMembers = useMemo(
    () => [team.leader, ...team.members].map((member) => member.toEntityMember()),
    [team]
  );

  // A failed load leaves the store empty, which reaches the empty state below and reads as a team
  // that has not published rates yet. A leader who has published some would be told their work is
  // gone, so the failure has to say it is a failure.
  useEffect(() => {
    if (isError) {
      enqueueSnackbar("Couldn't load your team's fee schedules", { variant: "error" });
    }
  }, [isError]);
  // #endregion

  function toggleScheduleEditor(scheduleKey: string) {
    const isOpening = openScheduleEditor !== scheduleKey;
    setOpenScheduleEditor(isOpening ? scheduleKey : undefined);
    // A form that is opened is a form that starts empty. See `newScheduleForm`.
    if (isOpening && scheduleKey === NEW_SCHEDULE_KEY) setNewScheduleForm((form) => form + 1);
  }

  function renderScheduleEditor(feeSchedule?: FeeSchedule) {
    return (
      <EditTeamFeeSchedule
        feeSchedule={feeSchedule}
        teamId={teamId}
        teamMembers={teamMembers}
        onSaved={() => setOpenScheduleEditor(undefined)}
      />
    );
  }

  async function handleDeleteClicked(feeSchedule: FeeSchedule) {
    const response = await confirm({
      title: "Delete fee schedule?",
      message: `"${feeSchedule.name}" will stop showing on your team's marketplace profile.`,
      okButtonText: "Delete",
    });
    if (response !== ConfirmResponse.Ok) return;

    setDeletingScheduleId(feeSchedule.id);
    // The editor is anchored to this entry, so it goes when the entry does.
    if (openScheduleEditor === feeSchedule.id.value) setOpenScheduleEditor(undefined);
    try {
      await deleteFeeSchedule({ teamId: teamId.value, scheduleId: feeSchedule.id.value }).unwrap();
      enqueueSnackbar("Deleted fee schedule", { variant: "success" });
    } catch (error) {
      console.error(error);
      enqueueSnackbar("Couldn't delete fee schedule. Please try again.", { variant: "error" });
    }
    setDeletingScheduleId(undefined);
  }

  // #region Render
  return (
    <MainContainer className={className}>
      <Typography variant="h6">Fee Schedules</Typography>
      {isLoading && <Loader />}
      {!isLoading && feeSchedules.length < 1 && (
        <EmptyText variant="body2">
          {isError
            ? "Couldn't load your team's fee schedules. Please try again."
            : "Publish your team's rates so clients can see them on your marketplace profile."}
        </EmptyText>
      )}
      {!isLoading && feeSchedules.length > 0 && (
        <ScheduleList>
          {feeSchedules.map((feeSchedule: FeeSchedule) => (
            <ScheduleEntry key={feeSchedule.id.value}>
              <ScheduleRow>
              <ListItemText
                primary={feeSchedule.name}
                secondary={
                  <SecondaryLine>
                    {feeSchedule.tags.map((tag) => (
                      <Chip key={tag.value} label={tag.value} size="small" variant="outlined" />
                    ))}
                    <Typography variant="body2" color="text.secondary" component="span">
                      {feeSchedule.categoryCountLabel}
                    </Typography>
                  </SecondaryLine>
                }
                slotProps={{ secondary: { component: "div" } }}
              />
              <ScheduleRowActions>
                <Tooltip title={
                  openScheduleEditor === feeSchedule.id.value ? "Close editor" : "Edit fee schedule"
                }>
                  <IconButton
                    aria-label={
                      openScheduleEditor === feeSchedule.id.value
                        ? `Close ${feeSchedule.name}`
                        : `Edit ${feeSchedule.name}`
                    }
                    onClick={() => toggleScheduleEditor(feeSchedule.id.value)}
                  >
                    {openScheduleEditor === feeSchedule.id.value ? <CloseIcon /> : <EditIcon />}
                  </IconButton>
                </Tooltip>
                <Tooltip title="Delete fee schedule">
                  {/* A disabled button fires no events of its own, so the tooltip needs a live
                      wrapper to hang off while a deletion is in flight. */}
                  <span>
                    <DeleteIconButton
                      aria-label={`Delete ${feeSchedule.name}`}
                      disabled={!!deletingScheduleId}
                      onClick={() => void handleDeleteClicked(feeSchedule)}
                    >
                      {deletingScheduleId?.isEqualTo(feeSchedule.id) ? (
                        <CircularProgress size={20} />
                      ) : (
                        <DeleteIcon />
                      )}
                    </DeleteIconButton>
                  </span>
                </Tooltip>
              </ScheduleRowActions>
            </ScheduleRow>
              <Collapse in={openScheduleEditor === feeSchedule.id.value} unmountOnExit>
                <InlineScheduleEditor>{renderScheduleEditor(feeSchedule)}</InlineScheduleEditor>
              </Collapse>
            </ScheduleEntry>
          ))}
        </ScheduleList>
      )}
      <AddButtonContainer>
        <Button
          startIcon={openScheduleEditor === NEW_SCHEDULE_KEY ? <CloseIcon /> : <AddIcon />}
          onClick={() => toggleScheduleEditor(NEW_SCHEDULE_KEY)}
        >
          {openScheduleEditor === NEW_SCHEDULE_KEY ? "Cancel New Fee Schedule" : "Add Fee Schedule"}
        </Button>
      </AddButtonContainer>
      {/* A schedule that does not exist yet has no entry to sit under, so its form opens at the end
          of the list it is about to join. */}
      <Collapse in={openScheduleEditor === NEW_SCHEDULE_KEY} unmountOnExit>
        <NewScheduleEditor key={newScheduleForm}>{renderScheduleEditor()}</NewScheduleEditor>
      </Collapse>
    </MainContainer>
  );
  // #endregion
}
